import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import ImageProposalEditor from './ImageProposalEditor';
import { normalizeImageSlot } from './contentLayout';
import './ImageManagerDialog.css';


const updatePlacement = (slot, patch) => {
  const current = slot.placement || {};
  const placement = {
    type: 'inline',
    align: current.align || 'center',
    width: current.width || { unit: 'percent', value: 60 },
    wrap: current.wrap || 'none',
    ...patch,
  };
  if (placement.align === 'full') {
    placement.width = { unit: 'percent', value: 100 };
    placement.wrap = 'none';
  } else if (placement.align === 'center') {
    placement.wrap = 'none';
  }
  return { ...slot, placement };
};

const ImageManagerDialog = ({
  open,
  slot: slotValue,
  onSave,
  onClose,
  onUploadImage,
  canEdit = false,
  canManageStructure = false,
  onDeleteSlot,
  onReturnToGallery,
}) => {
  const normalizedSlot = normalizeImageSlot(slotValue);
  const [draft, setDraft] = useState(normalizedSlot);
  const [activeTab, setActiveTab] = useState('images');
  const [selectedProposalId, setSelectedProposalId] = useState(
    normalizedSlot.proposals[0]?.id || null
  );
  const titleRef = useRef(null);
  const id = useId().replace(/:/g, '');
  const titleId = `image-manager-title-${id}`;
  const isInline = draft.placement?.type === 'inline';

  useEffect(() => {
    if (!open) return;
    const nextDraft = normalizeImageSlot(slotValue);
    setDraft(nextDraft);
    setActiveTab('images');
    setSelectedProposalId(nextDraft.proposals[0]?.id || null);
  }, [open, slotValue]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    titleRef.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (
      selectedProposalId &&
      !draft.proposals.some((proposal) => proposal.id === selectedProposalId)
    ) {
      setSelectedProposalId(draft.proposals[0]?.id || null);
    }
  }, [draft.proposals, selectedProposalId]);

  if (!open || !slotValue) return null;

  const tabs = [
    ...(isInline && canManageStructure
      ? [{ id: 'position', label: 'Position' }]
      : []),
    { id: 'images', label: 'Images et langues' },
    { id: 'captions', label: 'Légendes' },
  ];
  const activeTabLabel =
    tabs.find((tab) => tab.id === activeTab)?.label || 'Images et langues';

  const changeWidth = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return;
    setDraft(
      updatePlacement(draft, {
        width: { ...draft.placement.width, value: parsed },
      })
    );
  };

  return createPortal(
    <div className="image-manager-overlay" role="presentation">
      <section
        className="image-manager-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="image-manager-dialog-header">
          <div>
            <h2 id={titleId} ref={titleRef} tabIndex="-1">
              Gérer les images
            </h2>
            <p>
              {isInline
                ? 'Image placée au curseur dans le contenu'
                : 'Image stockée dans la galerie'}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Fermer sans enregistrer">
            Fermer
          </button>
        </header>

        <div className="image-manager-tabs" role="tablist" aria-label="Réglages de l’image">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              id={`image-manager-tab-${id}-${tab.id}`}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`image-manager-panel-${id}-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="image-manager-dialog-body">
          <section
            id={`image-manager-panel-${id}-${activeTab}`}
            role="tabpanel"
            aria-label={activeTabLabel}
            aria-labelledby={`image-manager-tab-${id}-${activeTab}`}
          >
            {activeTab === 'position' && isInline && (
              <div className="image-manager-position-grid">
                <label>
                  <span>Position</span>
                  <select
                    aria-label="Position de l’image"
                    value={draft.placement.align || 'center'}
                    onChange={(event) =>
                      setDraft(
                        updatePlacement(draft, { align: event.target.value })
                      )
                    }
                  >
                    <option value="left">Gauche</option>
                    <option value="center">Centre</option>
                    <option value="right">Droite</option>
                    <option value="full">Pleine largeur</option>
                  </select>
                </label>
                <label>
                  <span>Largeur</span>
                  <input
                    type="number"
                    aria-label="Largeur de l’image intégrée"
                    min={draft.placement.width?.unit === 'px' ? 80 : 10}
                    max={draft.placement.width?.unit === 'px' ? 2000 : 100}
                    disabled={draft.placement.align === 'full'}
                    value={draft.placement.width?.value ?? 60}
                    onChange={(event) => changeWidth(event.target.value)}
                  />
                </label>
                <label>
                  <span>Unité</span>
                  <select
                    aria-label="Unité de l’image intégrée"
                    disabled={draft.placement.align === 'full'}
                    value={draft.placement.width?.unit || 'percent'}
                    onChange={(event) =>
                      setDraft(
                        updatePlacement(draft, {
                          width: {
                            unit: event.target.value,
                            value: event.target.value === 'px' ? 320 : 60,
                          },
                        })
                      )
                    }
                  >
                    <option value="percent">%</option>
                    <option value="px">px</option>
                  </select>
                </label>
                <label>
                  <span>Habillage</span>
                  <select
                    aria-label="Habillage de l’image intégrée"
                    disabled={['center', 'full'].includes(
                      draft.placement.align
                    )}
                    value={draft.placement.wrap || 'none'}
                    onChange={(event) =>
                      setDraft(
                        updatePlacement(draft, { wrap: event.target.value })
                      )
                    }
                  >
                    <option value="none">Aucun</option>
                    <option value="around">Texte autour</option>
                  </select>
                </label>
              </div>
            )}

            {activeTab === 'images' && (
              <ImageProposalEditor
                slot={draft}
                onChange={setDraft}
                onUploadImage={onUploadImage}
                canEdit={canEdit}
                view="images"
              />
            )}

            {activeTab === 'captions' && (
              <ImageProposalEditor
                slot={draft}
                onChange={setDraft}
                canEdit={canEdit}
                view="captions"
                selectedProposalId={selectedProposalId}
                onSelectProposal={setSelectedProposalId}
              />
            )}

            {canManageStructure &&
              ((!isInline && activeTab === 'images') ||
                (isInline && activeTab === 'position')) && (
                  <div className="image-manager-structural-actions">
                    {isInline && onReturnToGallery && (
                      <button type="button" onClick={onReturnToGallery}>
                        Retirer du texte et conserver en galerie
                      </button>
                    )}
                    {onDeleteSlot && (
                      <button
                        type="button"
                        className="image-manager-danger-link"
                        onClick={() => {
                          if (
                            window.confirm(
                              'Supprimer définitivement cet emplacement et toutes ses images ?'
                            )
                          ) {
                            onDeleteSlot();
                          }
                        }}
                      >
                        Supprimer définitivement cet emplacement
                      </button>
                    )}
                  </div>
                )}
          </section>
        </div>

        <footer className="image-manager-dialog-footer">
          <button type="button" onClick={onClose}>
            Annuler
          </button>
          <button
            type="button"
            className="image-manager-save-button"
            disabled={!canEdit}
            onClick={() => onSave?.(draft)}
          >
            Enregistrer
          </button>
        </footer>
      </section>
    </div>,
    document.body
  );
};

export default ImageManagerDialog;
